//LIBRARIES
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import FlatButton from 'material-ui/FlatButton';
import Autorenew from '@material-ui/icons/AccountBalance';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { browserHistory } from 'react-router';
import { Row, Col } from 'antd';

//COMPONENTS
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import BamboraTenantPayment from './bambora/BamboraTenantPayment';
import PaysafeTenantPayment from './paysafe/PaysafeTenantPayment';
import AutopaySetupWarning from './AutopaySetupWarning';
import { BAMBORA, PAYSAFE } from 'components/tenant_app/components/payments/PaymentConstants';
import PaysafeDirectDebitPayment from './paysafe/PaysafeDirectDebitPayment';

//HELPERS
import { countFeaturesEnabled } from 'helpers/ToggleBot';
import { AccentColor } from 'helpers/AccentColorHelper';
import { getTranslatedString } from 'helpers/i18n';

//ACTIONS
import {
  makePayment,
  authorizePayment,
  makeDirectDebitPayment,
  authorizeDirectDebitPayment,
} from 'actions/tenantProfileActions';
import {
  acceptApplicationPayment,
  authorizeApplicationPayment,
  makeApplicationFeePayment,
  toggleApplicationFeePaymentModal,
} from 'actions/tenantApplicationActions';

const StyledH3 = styled.h3`
  margin: 10px 0 5px;
  text-align: center;
`;

const StyledH4 = styled.h4`
  margin: 0 0 15px;
  text-align: center;
`;

const StyledH4Bold = styled.h4`
  text-align: center;
  font-family: Circular-Bold;
  font-weight: 800;
  margin-top: 10px;
`;

const StyledPCenter = styled.p`
  text-align: center;
  margin-top: 10px;
`;

const StyledIconContainer = styled.div`
  width: 85px;
  height: 85px;
  margin: auto;
  text-align: center;
  border-radius: 100px;
  border: 4px solid ${AccentColor()};
  cursor: pointer;
  padding-top: 11px;
`;

class TenantPayDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      paymentDialogOpen: false,
      directDebitPaymentDialogOpen: false,
      activeAutopayWarningOpen: false,
      paymentType: null, // autopay or card
    };
  }

  openWarningDialog = () => {
    this.setState({ activeAutopayWarningOpen: true });
  };

  closeWarningDialog = () => {
    this.setState({ activeAutopayWarningOpen: false });
  };

  openPaymentDialog = () => {
    this.setState({ paymentDialogOpen: true });
  };

  closePaymentDialog = () => {
    this.setState({ paymentDialogOpen: false });
  };

  togglePaymentDialog = () => {
    this.setState({ paymentDialogOpen: !this.state.paymentDialogOpen });
    if (this.props.tenantApplication.has_application_fee) {
      this.props.toggleApplicationFeePaymentModal(false);
      browserHistory.push('/dashboard');
    }
  };

  toggleDirectDebitPaymentDialog = () => {
    this.setState({ directDebitPaymentDialogOpen: !this.state.directDebitPaymentDialogOpen });
  };

  closeWarningAndOpenPaymentDialog = () => {
    this.closeWarningDialog();
    this.openPaymentDialog();
  };

  payWithCard = () => {
    this.setState({ paymentType: 'card' });
    if (this.props.tenantOverview.external_autopay_setup_on_lease) {
      this.openWarningDialog();
    } else {
      this.openPaymentDialog();
    }
  };

  render() {
    const {
      header,
      subHeader,
      tenantOverview,
      authorizePayment,
      authorizeApplicationPayment,
      makeApplicationFeePayment,
      makeDirectDebitPayment,
      authorizeDirectDebitPayment,
      makePayment,
      hideClose,
      toggleDialog,
      fullScreen,
      paymentHold,
      isOpen,
      toggleAutopay,
      lockedAmount,
      showFeeInfo,
      hideAutoPayVerbiage,
    } = this.props;
    const {
      paymentDialogOpen,
      directDebitPaymentDialogOpen,
      activeAutopayWarningOpen,
      paymentType,
    } = this.state;
    const paymentDetails = this.props.paymentDetails || { historic_payments: [] };
    const {
      payment_provider,
      credit_card_fee,
      autopay_fee,
      absorb_credit_card_deposit_fee,
    } = tenantOverview;
    const { has_application_fee } = this.props.tenantApplication;

    let creditDetailsVerbiage = getTranslatedString('payment.creditDetails', {
      processingFee: credit_card_fee,
    });
    let creditDetailsTitle = getTranslatedString('payment.CreditCard');

    if (absorb_credit_card_deposit_fee && paymentDetails.lease_application_id) {
      // If it is an application deposit and landlord has absorb, show no fees in description
      creditDetailsTitle = getTranslatedString('payment.CreditCardNoFee');
      creditDetailsVerbiage = getTranslatedString('payment.creditDetailsNoFee');
    }

    let directDebitDetailsTitle = getTranslatedString('payment.directDebitFee');
    let directDebitDetailsVerbiage = getTranslatedString('payment.directDebitDetailsFee', {
      processingFee: autopay_fee,
    });
    if (!autopay_fee || autopay_fee === '$0.00') {
      directDebitDetailsTitle = getTranslatedString('payment.directDebitNoFee');
      directDebitDetailsVerbiage = getTranslatedString('payment.directDebitDetailsNoFee');
    }

    let autopayVerbiage = getTranslatedString('payment.autopayDetails', {
      autopayDay: tenantOverview.external_autopay_min_day,
    });
    if (payment_provider === BAMBORA) {
      creditDetailsVerbiage = getTranslatedString('payment.bambora.creditDetails', {
        processingFee: '1.5%',
      });
      creditDetailsTitle = getTranslatedString('payment.creditDebit');
      autopayVerbiage = getTranslatedString('payment.bambora.autopayDetails', {
        autopayDay: tenantOverview.external_autopay_min_day,
      });
    }

    let authorizePaymentAction = authorizePayment;
    let makePaymentAction = makePayment;
    let paymentOptionsCount = countFeaturesEnabled([
      'payments.autopay',
      'payments.direct_debit',
      'payments.credit_card',
    ]);
    let paymentOptionsClass = 24 / paymentOptionsCount;

    const paymentHeader = header ? header : getTranslatedString('makeAPayment');
    const paymentSubHeader = subHeader ? subHeader : getTranslatedString('paymentOptions');

    if (has_application_fee) {
      authorizePaymentAction = authorizeApplicationPayment;
      paymentOptionsClass = 24;
      makePaymentAction = makeApplicationFeePayment;
    }

    const isDepositPayment = paymentDetails.payment_type === 'deposit';
    if (isDepositPayment) {
      paymentOptionsClass = 12;
    }
    const showAutopay = has_application_fee || lockedAmount || isDepositPayment;

    return (
      <div>
        {payment_provider === BAMBORA && (
          <BamboraTenantPayment
            toggleParent={toggleDialog}
            open={paymentDialogOpen}
            toggleOpen={this.togglePaymentDialog}
            fullScreen={fullScreen}
            outstandingBalance={paymentDetails.outstanding_balance}
          />
        )}
        {payment_provider === PAYSAFE && (
          <Fragment>
            <PaysafeTenantPayment
              onSuccess={toggleDialog}
              open={paymentDialogOpen}
              toggleOpen={this.togglePaymentDialog}
              fullScreen={fullScreen}
              makePayment={makePaymentAction}
              authorizePayment={authorizePaymentAction}
              paymentType={paymentDetails.payment_type}
              outstandingBalance={paymentDetails.outstanding_balance}
              applicationId={paymentDetails.application_id}
              leaseApplicationId={paymentDetails.lease_application_id}
              paymentHold={paymentHold}
              lockedAmount={lockedAmount}
              showFeeInfo={showFeeInfo}
            />
            <PaysafeDirectDebitPayment
              onSuccess={toggleDialog}
              open={directDebitPaymentDialogOpen}
              toggleOpen={this.toggleDirectDebitPaymentDialog}
              fullScreen={fullScreen}
              makePayment={makeDirectDebitPayment}
              authorizePayment={authorizeDirectDebitPayment}
              outstandingBalance={paymentDetails.outstanding_balance}
              applicationId={paymentDetails.application_id}
              leaseApplicationId={paymentDetails.lease_application_id}
              paymentType={paymentDetails.payment_type}
              paymentHold={paymentHold}
              lockedAmount={lockedAmount}
              padDetailType={tenantOverview.pad_detail_type}
              showFeeInfo={showFeeInfo}
            />
          </Fragment>
        )}
        <AutopaySetupWarning
          handleCancel={this.closeWarningDialog}
          handleContinue={this.closeWarningAndOpenPaymentDialog}
          open={activeAutopayWarningOpen}
          fullScreen={fullScreen}
          paymentType={paymentType}
        />
        <Dialog
          open={isOpen}
          onClose={!hideClose && toggleDialog}
          aria-labelledby="payment-details-dialog"
          classes={{ paper: 'form-dialog' }}
          fullScreen={fullScreen}
        >
          <DialogTitle id="payment-details-dialog">
            <StyledH3>
              {paymentHeader}
              <b style={{ fontFamily: 'Circular-Black', color: AccentColor() }}>.</b>&nbsp;
            </StyledH3>
          </DialogTitle>
          <DialogContent>
            <div>
              <Fragment>
                <StyledH4> {paymentSubHeader} </StyledH4>
                <Row>
                  <RenderIfToggleOn toggle="payments.credit_card">
                    <Col
                      span={paymentOptionsClass}
                      id="hover-select"
                      style={{ marginTop: '15px' }}
                      onClick={this.payWithCard}
                    >
                      <StyledIconContainer>
                        <CreditCardIcon
                          style={{ color: AccentColor(), height: '55px', width: '55px' }}
                        />
                      </StyledIconContainer>
                      <StyledH4Bold>{creditDetailsTitle}</StyledH4Bold>
                      <StyledPCenter>{creditDetailsVerbiage}</StyledPCenter>
                    </Col>
                  </RenderIfToggleOn>
                  <RenderIfToggleOn
                    toggle="payments.direct_debit"
                    dontRenderIf={has_application_fee}
                  >
                    <Col
                      span={paymentOptionsClass}
                      id="hover-select"
                      style={{ marginTop: '15px' }}
                      onClick={this.toggleDirectDebitPaymentDialog}
                    >
                      <StyledIconContainer>
                        <Autorenew
                          style={{ color: AccentColor(), height: '55px', width: '55px' }}
                        />
                      </StyledIconContainer>
                      <StyledH4Bold>{directDebitDetailsTitle}</StyledH4Bold>
                      <StyledPCenter>{directDebitDetailsVerbiage}</StyledPCenter>
                    </Col>
                  </RenderIfToggleOn>
                  <RenderIfToggleOn toggle="payments.autopay" dontRenderIf={showAutopay}>
                    <Col
                      span={paymentOptionsClass}
                      id="hover-select"
                      style={{ marginTop: '15px' }}
                      onClick={toggleAutopay}
                    >
                      <StyledIconContainer>
                        <Autorenew
                          style={{ color: AccentColor(), height: '55px', width: '55px' }}
                        />
                      </StyledIconContainer>
                      <StyledH4Bold>{getTranslatedString('payment.autopay')}</StyledH4Bold>
                      <StyledPCenter>{autopayVerbiage}</StyledPCenter>
                    </Col>
                  </RenderIfToggleOn>
                </Row>
                {tenantOverview.has_active_pad &&
                  !hideAutoPayVerbiage && (
                    <div className="row" style={{ marginTop: '20px' }}>
                      <p style={{ textAlign: 'justify' }}>
                        {' '}
                        <i className="em em-raised_hands" />
                        <b style={{ fontFamily: 'Circular-Black', color: AccentColor() }}>
                          {' '}
                          {getTranslatedString('headsUp')}!{' '}
                        </b>
                        <i className="em em-raised_hands" />
                        &nbsp;
                        {getTranslatedString('autopay.autopayActiveWarning')}
                      </p>
                    </div>
                  )}
              </Fragment>
            </div>
          </DialogContent>
          <DialogActions style={{ display: 'block' }}>
            {!hideClose && (
              <FlatButton onClick={toggleDialog} id="close-button" style={{ color: AccentColor() }}>
                {getTranslatedString('close')}
              </FlatButton>
            )}
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withMobileDialog()(
  connect(
    store => {
      return {
        tenantOverview: store.dashboard.tenantOverview,
      };
    },
    {
      makePayment,
      authorizePayment,
      acceptApplicationPayment,
      authorizeApplicationPayment,
      makeDirectDebitPayment,
      authorizeDirectDebitPayment,
      makeApplicationFeePayment,
      toggleApplicationFeePaymentModal,
    }
  )(TenantPayDialog)
);
